/**
 * Sub-Collection Links Slider
 * Navigation and scroll state management
 */

class SubCollectionLinksSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('[data-scl-track]');
    this.prevBtn = container.querySelector('[data-scl-prev]');
    this.nextBtn = container.querySelector('[data-scl-next]');
    
    if (!this.track) return;
    
    this.scrollAmount = 0;
    this.isDown = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.hasDragged = false;
    this.dragThreshold = 10; // Minimum pixels to consider it a drag
    this.mouseDownX = 0;
    this.mouseDownY = 0;
    this.isClickingLink = false;
    this.init();
  }
  
  init() {
    this.calculateScrollAmount();
    this.bindEvents();
    this.updateArrowStates();
  }
  
  calculateScrollAmount() {
    const card = this.track.querySelector('.scl__card');
    if (card) {
      const cardWidth = card.offsetWidth;
      const gap = parseInt(getComputedStyle(this.track).gap) || 12;
      // Scroll by 3 cards at a time
      this.scrollAmount = (cardWidth + gap) * 3;
    }
  }
  
  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scroll('prev'));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scroll('next'));
    }
    
    // Drag to scroll functionality - only on track background, not on links
    this.track.addEventListener('mousedown', (e) => {
      // Store mouse position for drag detection
      this.mouseDownX = e.pageX;
      this.mouseDownY = e.pageY;
      
      // Allow links to work normally - don't start drag if clicking directly on a link
      if (e.target.closest('a.scl__card')) {
        this.isClickingLink = true;
        return;
      }
      this.isClickingLink = false;
      this.handleMouseDown(e);
    });
    
    this.track.addEventListener('mouseleave', () => this.handleMouseUp());
    this.track.addEventListener('mouseup', (e) => {
      // Check if mouse moved significantly
      if (this.mouseDownX !== 0) {
        const deltaX = Math.abs(e.pageX - this.mouseDownX);
        const deltaY = Math.abs(e.pageY - this.mouseDownY);
        // Only consider it a drag if moved more than threshold horizontally
        // and not much vertically (to distinguish from scrolling)
        if (deltaX > this.dragThreshold && deltaX > deltaY) {
          this.hasDragged = true;
        } else {
          this.hasDragged = false;
        }
      }
      this.handleMouseUp();
    });
    this.track.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    
    // Prevent clicks only on track background if user dragged
    // Never prevent link clicks - they should always work
    this.track.addEventListener('click', (e) => {
      // Always allow link clicks - never prevent them
      if (e.target.closest('a.scl__card')) {
        this.hasDragged = false;
        return; // Let link work normally
      }
      
      // Only prevent clicks on track background (not links) if we dragged
      if (this.hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.hasDragged = false;
    }, true);
    
    let scrollTimeout;
    this.track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.updateArrowStates(), 50);
    }, { passive: true });
    
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.calculateScrollAmount();
        this.updateArrowStates();
      }, 100);
    });
  }
  
  handleMouseDown(e) {
    this.isDown = true;
    this.track.classList.add('dragging');
    this.startX = e.pageX - this.track.offsetLeft;
    this.scrollLeft = this.track.scrollLeft;
    this.initialScrollLeft = this.track.scrollLeft;
  }
  
  handleMouseUp() {
    this.isDown = false;
    this.track.classList.remove('dragging');
    this.mouseDownX = 0;
    this.mouseDownY = 0;
  }
  
  handleMouseMove(e) {
    if (!this.isDown) return;
    e.preventDefault();
    const x = e.pageX - this.track.offsetLeft;
    const walk = (x - this.startX) * 2; // Scroll speed multiplier
    this.track.scrollLeft = this.scrollLeft - walk;
    
    // Mark as dragging if moved more than threshold
    const movedDistance = Math.abs(e.pageX - this.startX);
    if (movedDistance > this.dragThreshold) {
      this.hasDragged = true;
    }
  }
  
  scroll(direction) {
    const scrollValue = direction === 'prev' ? -this.scrollAmount : this.scrollAmount;
    
    this.track.scrollBy({
      left: scrollValue,
      behavior: 'smooth'
    });
  }
  
  updateArrowStates() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const isAtStart = scrollLeft <= 2;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    
    this.prevBtn.disabled = isAtStart;
    this.nextBtn.disabled = isAtEnd;
  }
}

// Initialize all sliders
function initSubCollectionLinks() {
  const sliders = document.querySelectorAll('[data-sub-collection-links]');
  
  // Hide custom-section if slider is present
  if (sliders.length > 0) {
    const customSection = document.querySelector('#custom-section');
    if (customSection) {
      customSection.style.display = 'none';
    }
    
    // Also watch for dynamically created custom-section
    const observer = new MutationObserver(() => {
      const customSection = document.querySelector('#custom-section');
      if (customSection && sliders.length > 0) {
        customSection.style.display = 'none';
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  sliders.forEach(container => {
    if (!container.dataset.initialized) {
      new SubCollectionLinksSlider(container);
      container.dataset.initialized = 'true';
    }
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubCollectionLinks);
} else {
  initSubCollectionLinks();
}

// Shopify section events for theme editor
document.addEventListener('shopify:section:load', initSubCollectionLinks);

